import { useState, useEffect, useRef, useContext } from "react";
import { formatTimestamp, isWithinTimeRange, timePassed } from "../utils";
import { LiaBusinessTimeSolid } from "react-icons/lia";
import { BsTag } from "react-icons/bs";
import { LuMessageSquare } from "react-icons/lu";
import { IoIosPerson } from "react-icons/io";
import { MdKeyboardArrowDown } from "react-icons/md";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { MdNotificationsNone } from "react-icons/md";
import { AccountContext } from "../contexts/AccountContexts";
import { NotificationContext } from "../contexts/NotificationContexts";
import { onNewNotification } from "../utils/notificationUtils";
import { requestNotifications } from "../requests/notification_requests";
import { ContentContext } from "../contexts/ContentContext";
import { useTranslation, Trans } from "react-i18next";
import { componentLoadingActive, ComponentLoadingScreen } from "./Background";
import { darkAndLightThemeDefault } from "./ThemeComponents";
import useNotificationSounds from "./AudioComponents";
import { WarningIcon, WarningNotificationText, ticketLink, warningExcerpt, warningSummary } from "./WarningComponents";
import { PermissionContext } from "../contexts/PermissionContexts";

export const WorkingHoursNotifier = ({ resetHook }) => {
    const { t } = useTranslation('edit')
    const [isWorkingHours, setIsWorkingHours] = useState(true);


    useEffect(() => {
        setIsWorkingHours(isWithinTimeRange(10, 19))
    }, [resetHook])

    return (

        !isWorkingHours &&
        <div role="alert" className="alert alert-warning dark:bg-yellow-700 dark:text-gray-300">
            <LiaBusinessTimeSolid size={24} />
            <span className="">{t('Your message will be replied to during office hours: 10:00-19:00 (UTC +3)')}</span>
        </div>

    )
}

export const GlobalNotificationComponent = () => {
    const { t } = useTranslation('common')
    const { socket, numberNotifications, numberWarningNotifications, reconnected, showNotification, isTabActive } = useContext(NotificationContext)
    const { devs, userPreferences } = useContext(AccountContext);
    const { hasPermission } = useContext(PermissionContext)
    const location = useLocation()
    const isEditPage = location.pathname.includes('/edit')

    const [newNotifications, setNewNotifications] = useState([])

    const soundOn = userPreferences ? userPreferences.notification_sound_on : false
    const desktopNotificationsOn = userPreferences?.enable_desktop_notifications ?? true

    const playSound = useNotificationSounds()
    useEffect(() => {
        const playNotificationSound = (type) => {
            if (!soundOn) return
            playSound(type)
        }
        const showNotificationCallback = (notification) => {
            if (!desktopNotificationsOn) return

            if (!isTabActive) {
                let title = ""
                let body = ""
                if (notification.type === "assignment") {
                    title = t("notification_type.assignment")
                    body = t(notification.by && devs[notification.by] ? "desktop_notification.assignment.manual" : "desktop_notification.assignment.auto", { ticket_id: notification.ticket_created_at.toString(), user: notification.by && devs[notification.by] ? devs[notification.by].name : "" })
                }
                else if (notification.type === "new_message") {
                    title = t("notification_type.new_message")
                    body = t("desktop_notification.new_message", { ticket_id: notification.ticket_created_at.toString() })
                }
                else if (notification.type === "tag") {
                    title = t("notification_type.tag")
                    body = t("desktop_notification.tag", { ticket_id: notification.ticket_created_at.toString() })
                }
                else if (notification.type === "warning") {
                    title = t("notification_type.warning")
                    body = warningSummary(t, notification, notification.details?.role)
                    if (warningExcerpt(notification)) body += `\n"${warningExcerpt(notification)}"`
                }
                showNotification(title, body)
            }
        }

        onNewNotification({ socket: socket, setNewNotifications: setNewNotifications, playSound: playNotificationSound, showNotification: showNotificationCallback })
    }, [userPreferences, reconnected, isTabActive])

    // Warnings and the other notifications have separate boxes, so they don't get mixed up.
    return (
        <div className="">
            <div className={`fixed ltr:right-2 rtl:left-2 z-50 ${isEditPage ? "top-0" : "top-2"}`}>
                <div className="flex items-start justify-end gap-2">
                    <HeaderNotificationBox
                        kind="warnings"
                        count={numberWarningNotifications}
                        title={t("warning.header_title")}
                        icon={<WarningIcon size={16} />}
                        mobileIcon={<WarningIcon size={22} />}
                        emptyText={t("warning.header_empty")}
                        showAllText={t("warning.header_show_all")}
                        // admins and mods: everyone's warnings; other employees: their own
                        showAllTo={hasPermission("warnings:view") ? "/admin/warnings" : "/admin/my-warnings"}
                    />
                    <HeaderNotificationBox
                        kind="others"
                        count={numberNotifications}
                        title={t('Notifications')}
                        icon={<MdNotificationsNone size={16} />}
                        mobileIcon={<MdNotificationsNone size={24} />}
                        emptyText={t('No new notifications')}
                        showAllText={t('Show all notifications')}
                        showAllTo="/admin/notifications"
                    />
                </div>
                {
                    newNotifications.map((notification, index) =>

                        <NewNotification key={index} notification={notification} index={index} />
                    )
                }
            </div>
        </div>
    )
}


// One box of the header: its unread count, and a list of the unread items of its kind when opened.
const HeaderNotificationBox = ({ kind, count, title, icon, mobileIcon, emptyText, showAllText, showAllTo }) => {
    const { openErrorModal } = useContext(ContentContext)
    const { socket } = useContext(NotificationContext)
    const [notifications, setNotifications] = useState([])
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef(null)
    const navigator = useNavigate()
    const loadingTag = `notification-bar-${kind}`

    const handleMenuOpen = () => {
        if (!menuOpen) {
            componentLoadingActive(true, loadingTag)
            requestNotifications({ nFirst: 30, unread_only: true, kind })
                .then((data) => {
                    setNotifications(data.notifications)
                    componentLoadingActive(false, loadingTag)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    componentLoadingActive(false, loadingTag)
                })
        }
        setMenuOpen(!menuOpen)
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleClick = (e) => {
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
            navigator(showAllTo)
        }
    }

    // Warnings are read only here (opening a ticket reads its notifications, not its warnings):
    // one at a time, when it is opened. There is no "mark all as read" on purpose.
    const readWarnings = (ids) => socket.emit("read_warning_notifications", { ids })

    return (
        <div className={`menu bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 border ${menuOpen ? "sm:w-96" : "sm:min-w-64"} p-0 rounded-full sm:rounded-lg cursor-pointer relative ${!menuOpen && "hover:border-primary"} shadow-md sm:shadow-none w-[44px] h-[44px] sm:w-auto sm:h-auto flex items-center justify-center sm:block`}
            onClick={handleMenuOpen}
            ref={menuRef}
        >
            <ComponentLoadingScreen tag={loadingTag} />
            <div className={`text-md font-medium w-full h-full flex sm:grid sm:grid-cols-6 items-center justify-center rounded-full sm:rounded-lg select-none ${darkAndLightThemeDefault} sm:p-2 sm:px-4`}>
                <div className="hidden sm:block min-w-8 col-span-1 place-items-start">
                    <div className={`w-6 h-6 rounded-lg ${count > 0 ? (kind === "warnings" ? "text-white bg-red-600" : "text-white bg-primary dark:text-blue-600 dark:bg-gray-200") : "invisible"} flex items-center justify-center select-none text-sm`}>
                        {count}
                    </div>
                </div>

                {/* Mobile Badge */}
                {count > 0 && (
                    <div className="sm:hidden absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex flex-col items-center justify-center text-[10px] font-bold z-10 border-[1.5px] border-white dark:border-gray-700 shadow-sm leading-none">
                        <span className="mt-[1px]">{count}</span>
                    </div>
                )}

                <div className="sm:col-span-4 flex items-center justify-center">
                    {/* Mobile Icon Only */}
                    <div className="sm:hidden flex items-center justify-center text-gray-700 dark:text-gray-200">
                        {mobileIcon}
                    </div>

                    {/* Desktop Icon + Text */}
                    <div className="hidden sm:flex items-center gap-1">
                        {icon}
                        <span>{title}</span>
                    </div>
                </div>
                <div className={`hidden sm:flex justify-end`}>
                    <div className={`${menuOpen && "rotate-180"} transition-all`}>
                        <MdKeyboardArrowDown size={20} />
                    </div>
                </div>
            </div>
            {
                menuOpen &&
                <div className="absolute top-full right-0 mt-3 sm:static sm:mt-0 w-[calc(100vw-24px)] max-w-[360px] sm:max-w-none sm:w-full bg-white dark:bg-gray-700 sm:bg-transparent rounded-xl sm:rounded-b-lg sm:rounded-t-none shadow-2xl sm:shadow-none border border-gray-200 dark:border-gray-600 sm:border-0 z-50 flex flex-col overflow-hidden text-left origin-top-right">
                    <div className="flex flex-col max-h-[400px] overflow-y-auto overscroll-contain relative p-1.5 sm:p-0">
                        {
                            notifications.map((notification, index) =>
                                <NotificationElement key={index} notification={notification}
                                    onOpen={kind === "warnings" ? () => readWarnings([notification.id]) : undefined} />
                            )
                        }
                        {
                            notifications.length === 0 &&
                            <div className="flex justify-center w-full py-6">
                                <span className="text-sm ltr:italic text-gray-500">{emptyText}</span>
                            </div>
                        }
                    </div>
                    <div className="flex border-t border-gray-200 sm:border-gray-300">
                        {
                            showAllTo &&
                            <a className={`flex flex-1 justify-center p-3 sm:p-1 bg-gray-50 sm:bg-white dark:bg-gray-800 ${darkAndLightThemeDefault} sm:rounded-b-lg w-full text-center hover:bg-sky-50 dark:hover:bg-sky-800 cursor-pointer font-medium text-sm transition-colors`}
                                onClick={handleClick}
                            >{showAllText}</a>
                        }
                    </div>
                </div>
            }
        </div>
    )
}



export const NewNotification = ({ notification, index }) => {
    const { t } = useTranslation('common')
    const { devs } = useContext(AccountContext)
    const [show, setShow] = useState(true)

    useEffect(() => {
        let deadline = notification.expires - Date.now()
        if (deadline > 0) {
            setShow(true)
        }
        else {
            setShow(false)
        }

        setTimeout(() => {

            setShow(false)
        }, deadline)


    }, [])

    const handleClick = (e) => {
        e.preventDefault()
        setShow(false)
    }


    return (
        <div className={`mt-1 flex w-full border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-300 rounded-md  p-1 gap-2 px-4 group bg-white dark:bg-gray-700 select-none
        ${show ? "translate-y-0 opacity-100 " : "-translate-y-2 opacity-0 hidden"} transition-all duration-300
        `}
            onClick={handleClick}
        >
            <div className={`grid grid-cols-1 items-center`}>
                <div className={`w-6 h-6 rounded-full 
                    ${(notification.type === "assignment") && "bg-blue-700" ||
                    (notification.type === "new_message") && "bg-green-700" ||
                    (notification.type === "tag") && "bg-yellow-500" ||
                    (notification.type === "warning") && "bg-red-600"
                    }

                    flex items-center justify-center text-white text-md select-none`}>
                    {
                        (notification.type === "assignment") && <IoIosPerson />
                        ||
                        (notification.type === "new_message") && <LuMessageSquare />
                        ||
                        (notification.type === "tag") && <BsTag />
                        ||
                        (notification.type === "warning") && <WarningIcon />
                    }
                </div>
            </div>
            <div className="flex flex-col flex-1 text-sm">
                <span className="flex items-center gap-1">
                    {/* {parseMessage(notification.message, notification.ticket_created_at.toString(), devs)} */}
                    {
                        (notification.type === "warning") && <WarningNotificationText notification={notification} />
                        ||
                        (notification.type === "assignment") && <Trans t={t} i18nKey={notification.by && devs[notification.by] ? "notification.assignment.manual" : "notification.assignment.auto"} values={{ ticket_id: notification.ticket_created_at.toString(), user: notification.by && devs[notification.by] ? devs[notification.by].name : "" }} components={{
                            u: <u className="text-primary dark:text-blue-400" />,
                            em: <span className="text-fuchsia-800 dark:text-fuchsia-400 font-semibold" />,
                            span: <span />
                        }} />
                        ||
                        (notification.type === "new_message") &&
                        <Trans t={t} i18nKey={"notification.new_message"} values={{ ticket_id: notification.ticket_created_at.toString() }} components={{
                            u: <u className="text-primary dark:text-blue-400" />,
                            span: <span />
                        }} />
                        ||
                        (notification.type === "tag") && <Trans t={t} i18nKey={"notification.tag"} values={{ ticket_id: notification.ticket_created_at.toString() }} components={{
                            u: <u className="text-primary dark:text-blue-400" />,
                            span: <span />
                        }} />
                    }
                </span>
            </div>
        </div>
    )
}



// onOpen: called when the notification is opened (the warnings box marks the warning read with it)
export const NotificationElement = ({ notification, inPage = false, onOpen }) => {
    const { t } = useTranslation('common')
    const { devs } = useContext(AccountContext)

    const timestamp = formatTimestamp(notification.timestamp * 1000)
    const _timepassed = timePassed(notification.timestamp * 1000, t)
    const navigator = useNavigate()

    const to = notification.type === "warning" ? ticketLink(notification.ticket_code) : `/admin/${notification.ticket_code}/edit`


    const handleClick = (e) => {
        if (onOpen) onOpen()
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
            // e.preventDefault();
            navigator(to)
        }
    }

    return (
        <NavLink className={`flex w-full border border-gray-300 dark:border-gray-600 rounded-sm text-gray-800 dark:text-gray-300 p-1 group hover:border-primary  cursor-pointer
            ${notification.seen ? "bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700" : "bg-white hover:bg-sky-50 dark:bg-gray-600 dark:hover:bg-sky-800"}
             ${inPage ? "gap-3 px-4" : "gap-2 px-2"}
            `}
            // href={to}
            onClick={handleClick}
            // target="_self"
            to={to}
        >
            {
                // inPage &&
                <div className="flex w-3 items-center justify-center">
                    <span className="relative flex h-3 w-3">
                        <span className={`${notification.read ? "" : "animate-ping"}  absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75`}></span>
                        <span className={`${notification.read ? "bg-gray-400" : "bg-sky-500"} relative inline-flex rounded-full h-3 w-3 `}></span>
                    </span>
                </div>

            }

            <div className={`grid grid-cols-1 items-center w-max`}>
                <div className={`w-6 h-6 rounded-full 
                    ${(notification.type === "assignment") && "bg-blue-700" ||
                    (notification.type === "new_message") && "bg-green-700" ||
                    (notification.type === "tag") && "bg-yellow-500" ||
                    (notification.type === "warning") && "bg-red-600"
                    }

                    flex items-center justify-center text-white text-md select-none`}>
                    {
                        (notification.type === "assignment") && <IoIosPerson />
                        ||
                        (notification.type === "new_message") && <LuMessageSquare />
                        ||
                        (notification.type === "tag") && <BsTag />
                        ||
                        (notification.type === "warning") && <WarningIcon />
                    }
                </div>
            </div>
            <div className="flex flex-col flex-1">
                <span className="flex items-start gap-1">
                    {
                        (notification.type === "warning") && <WarningNotificationText notification={notification} />
                        ||
                        (notification.type === "assignment") && <Trans t={t} i18nKey={notification.by && devs[notification.by] ? "notification.assignment.manual" : "notification.assignment.auto"} values={{ ticket_id: notification.ticket_created_at.toString(), user: notification.by && devs[notification.by] ? devs[notification.by].name : "" }} components={{
                            u: <u className="text-primary dark:text-blue-400" />,
                            em: <span className="text-fuchsia-800 dark:text-fuchsia-400 font-semibold" />,
                            span: <span />
                        }} />
                        ||
                        (notification.type === "new_message") &&
                        <Trans t={t} i18nKey={"notification.new_message"} values={{ ticket_id: notification.ticket_created_at.toString() }} components={{
                            u: <u className="text-primary dark:text-blue-400" />,
                            span: <span />
                        }} />
                        ||
                        (notification.type === "tag") && <Trans t={t} i18nKey={"notification.tag"} values={{ ticket_id: notification.ticket_created_at.toString() }} components={{
                            u: <u className="text-primary dark:text-blue-400" />,
                            span: <span />
                        }} />
                    }
                    {/* {parseMessage(notification.message, notification.ticket_created_at.toString(), devs, inPage)} */}
                </span>
                <div className="flex justify-between gap-1">
                    <span className="">{_timepassed}</span>
                    <span className="">{timestamp}</span>
                </div>
            </div>

        </NavLink>
    )
}


const composeNotification = () => {



}


const parseMessage = (message, ticketId, devs, inPage = false) => {
    // const parts = message.split(/(@\[[^\]]+\]\w+|\@\w+|\@[a-zA-Z0-9_\-]+)/g);
    // const parts = message.split(/(@\[[^\]]+\]\w+|\@\w+|\@[a-zA-Z0-9_]+)/g);
    const parts = message.split(/(@\[[^\]]+\]\w+|@\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|\@\w+)/g);
    return parts.map((part, index) => {
        // Check if the part starts with '@' indicating a tagged user
        if (part.startsWith('@')) {
            const ticketIdInText = part.slice(1); // Extract username without '@'
            const taggedTicketId = ticketId === ticketIdInText
            const taggedUser = Object.values(devs).find(user => user.name === ticketIdInText);
            if (taggedTicketId) {
                // Return the TaggedUserComponent if user is found
                return <HighlightedTicketId key={index} ticketId={ticketId} inPage={inPage} />;
            }
            else if (taggedUser) {
                return <HighlightedUsername key={index} username={ticketIdInText} />
            }
        }
        // Return plain text otherwise
        return <span key={index} className="whitespace-pre-wrap text-nowrap">{part}</span>;
    });
};

const HighlightedTicketId = ({ ticketId, inPage }) => {

    return (
        <span className={`text-primary underline ${!inPage && " max-w-24 block truncate overflow-clip"}`}>{ticketId}</span>
    )
}


const HighlightedUsername = ({ username }) => {

    return (
        <span className="text-fuchsia-800 font-semibold">{username}</span>
    )
}


export const NotificationType = ({ notificationType }) => {
    const { t } = useTranslation('common')
    return (
        <div className="flex items-center gap-2 flex-nowrap w-full text-sm min-w-32">
            <div className={`w-5 h-5 rounded-full 
                    ${notificationTypeDict[notificationType].color} 
                    
                    flex items-center justify-center text-white text-md select-none`}>
                {notificationTypeDict[notificationType].icon}
            </div>
            <span>{t(notificationTypeDict[notificationType].name)}</span>
        </div>
    )
}

const notifIconSize = 14
const notificationTypeDict = {
    assignment: {
        name: "Assignment",
        icon: <IoIosPerson size={notifIconSize} />,
        color: "bg-blue-700",
    },
    new_message: {
        name: "New Message",
        icon: <LuMessageSquare size={notifIconSize} />,
        color: "bg-green-700",
    },
    warning: {
        name: "Warning",
        icon: <WarningIcon size={notifIconSize} />,
        color: "bg-red-600",
    },
    tag: {
        name: "Tag",
        icon: <BsTag size={notifIconSize} />,
        color: "bg-yellow-500",
    },
}